import { useErrorContext } from '@customink/project-service-react-components';
import { Context, getRollbarFromContext } from '@rollbar/react';
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import type Rollbar from 'rollbar';

export interface ErrorBoundaryProps {
  children: ReactNode;
  errorHandler?: Error;
}

export interface ErrorBoundaryState {
  hasError: boolean;
}

/*
  The Project Service prefers functional components to class components.
  The ErrorBoundary is the one exception to this rule,
  because only class components may be error boundaries.
*/

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static contextType = Context;
  rollbar: Rollbar | undefined;
  public state: ErrorBoundaryState = {
    hasError: false,
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidMount(): void {
    this.rollbar = getRollbarFromContext(this.context);
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.rollbar?.error(error, errorInfo);
  }

  public render() {
    const { children } = this.props;
    return this.state.hasError ? (
      <div data-testid="editor-error-boundary">
        Something went wrong in the application, please try again later.
      </div>
    ) : (
      children
    );
  }
}

// eslint-disable-next-line react/no-multi-comp
export default function ErrorBoundaryWithHandler({
  children,
}: {
  children: ReactNode;
}) {
  const errorHandler = useErrorContext();

  return <ErrorBoundary errorHandler={errorHandler}>{children}</ErrorBoundary>;
}
